import { getServerTime } from "@/utils/server-time";

import type { CardStatus, CardWithStatus, TicketConfig, Week, Winner, WinnerForUI } from "./types";

export function getWeekBoundaries() {
  const now = new Date(getServerTime());
  const day = now.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(now);
  monday.setUTCDate(now.getUTCDate() + diffToMonday);
  monday.setUTCHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  sunday.setUTCHours(23, 59, 59, 999);

  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    weekStart: monday.toISOString().split("T")[0]!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    weekEnd: sunday.toISOString().split("T")[0]!,
  };
}

export function getWeekDayIndices() {
  const { weekStart, weekEnd } = getWeekBoundaries();
  const msPerDay = 24 * 60 * 60 * 1000;

  return {
    weekStartIndex: Math.floor(new Date(weekStart).getTime() / msPerDay),
    weekEndIndex: Math.floor(new Date(weekEnd).getTime() / msPerDay),
  };
}

/** Qualifying volume at which the next ticket lands. Tickets are computed server-side. */
export function nextTicketThreshold(tickets: number, config: TicketConfig): number | null {
  if (tickets >= config.maxTickets) return null;
  return (tickets + 1) * config.ticketSizeUsd;
}

export function getCurrentWeek(weeks: Week[], currentWeekNumber: number): Week | undefined {
  return weeks.find((w) => w.week === currentWeekNumber);
}

export interface WeekWinnerPair {
  week: Week;
  winner: Winner;
}

export function getWinners(weeks: Week[]): WeekWinnerPair[] {
  const pairs: WeekWinnerPair[] = [];
  for (const week of weeks) {
    for (const winner of week.winners) {
      pairs.push({ week, winner });
    }
  }
  return pairs.sort((a, b) => {
    if (a.week.week !== b.week.week) return b.week.week - a.week.week;
    const cardA = a.week.cards.find((c) => c.id === a.winner.card_id);
    const cardB = b.week.cards.find((c) => c.id === b.winner.card_id);
    return (cardB?.value ?? 0) - (cardA?.value ?? 0);
  });
}

export function getAllCards(weeks: Week[], currentWeekNumber: number): CardWithStatus[] {
  const revealedCards: CardWithStatus[] = [];
  const unrevealedCards: CardWithStatus[] = [];

  const sortedWeeks = [...weeks].sort((a, b) => b.week - a.week);

  for (const week of sortedWeeks) {
    let status: CardStatus;
    if (week.week > currentWeekNumber) {
      status = "unrevealed";
    } else if (week.week < currentWeekNumber) {
      status = week.winners.length > 0 ? "past" : "available";
    } else {
      status = "this-week";
    }

    for (const card of week.cards) {
      const cardWithStatus = { ...card, status, week: week.week };
      if (status === "unrevealed") {
        unrevealedCards.push(cardWithStatus);
      } else {
        revealedCards.push(cardWithStatus);
      }
    }
  }

  return [...revealedCards, ...unrevealedCards];
}

export function getWinnersForUI(weeks: Week[]): WinnerForUI[] {
  return getWinners(weeks).map(({ week, winner }) => {
    const card = week.cards.find((c) => c.id === winner.card_id);
    return {
      week: week.week,
      cardName: card ? `${card.pokemon} ${card.set}` : "Unknown",
      cardImage: card?.image_url,
      grade: card ? `${card.grader.toUpperCase()} ${card.grade}` : "",
      gradeType: (card?.grader.toLowerCase() ?? "psa") as "psa" | "cgc",
      wallet: winner.wallet,
      externalWallet: winner.external_wallet,
      subaccount: winner.subaccount,
      tickets: winner.tickets,
      status: winner.claim_status,
      link: winner.link,
      tweetLink: winner.tweet_link,
    };
  });
}
